import React, { useState, useCallback, useRef, memo, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Camera,
  Image,
  Upload,
  X,
  Check,
  AlertCircle,
  RefreshCw,
  FileText,
  Trash2,
  Eye,
  ChevronDown,
  ChevronUp,
  Smartphone,
  FolderOpen
} from 'lucide-react';
import { useLazyImage } from '@/hooks/usePerformance';

const API = process.env.REACT_APP_BACKEND_URL;

// Lazy Loading Evidence Image Component
const LazyEvidenceImage = memo(function LazyEvidenceImage({ src, alt, className }) {
  const { ref, src: currentSrc, loaded, error } = useLazyImage(src);
  
  return (
    <div ref={ref} className={`relative ${className}`}>
      {!loaded && !error && (
        <div className="absolute inset-0 bg-slate-200 animate-pulse" />
      )}
      {error ? (
        <div className="w-full h-full flex items-center justify-center bg-slate-100">
          <AlertCircle className="h-6 w-6 text-slate-400" />
        </div>
      ) : (
        <img 
          src={currentSrc}
          alt={alt}
          className={`w-full h-full object-cover transition-opacity ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
    </div>
  );
});

// Evidence categories with required flags
const EVIDENCE_CATEGORIES = {
  before: { label: 'Before Photos', icon: Camera, required: true, color: 'bg-blue-500' },
  during: { label: 'During Photos', icon: Camera, required: false, color: 'bg-amber-500' },
  after: { label: 'After Photos', icon: Camera, required: true, color: 'bg-emerald-500' },
  sat_note: { label: 'SAT Note', icon: FileText, required: false, color: 'bg-purple-500' },
  certificate: { label: 'Certificate', icon: FileText, required: false, color: 'bg-teal-500' },
  invoice: { label: 'Invoice', icon: FileText, required: false, color: 'bg-slate-500' },
  quote: { label: 'Quote', icon: FileText, required: false, color: 'bg-orange-500' },
  other: { label: 'Other', icon: FileText, required: false, color: 'bg-gray-500' }
};

// File states
const FILE_STATES = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  COMPLETE: 'complete',
  ERROR: 'error'
};

/**
 * Compress image before upload
 * @param {File} file - Original file
 * @param {number} maxWidth - Max width (default 1920)
 * @param {number} quality - JPEG quality 0-1 (default 0.8)
 * @returns {Promise<Blob>} Compressed blob
 */
async function compressImage(file, maxWidth = 1920, quality = 0.8) {
  return new Promise((resolve, reject) => {
    // Only compress images
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }
    
    const img = new window.Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    img.onload = () => {
      // Calculate new dimensions
      let width = img.width;
      let height = img.height;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            resolve(file); // Fallback to original
          }
        },
        'image/jpeg',
        quality
      );
    };
    
    img.onerror = () => resolve(file); // Fallback on error
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Generate thumbnail preview
 */
function generateThumbnail(file) {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) {
      resolve(null);
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

/**
 * Single file upload item with progress
 */
const UploadItem = memo(function UploadItem({ 
  file, 
  thumbnail, 
  progress, 
  state, 
  category,
  onRemove, 
  onRetry 
}) {
  const categoryConfig = EVIDENCE_CATEGORIES[category] || EVIDENCE_CATEGORIES.other;
  
  return (
    <div 
      className={`relative flex items-center gap-3 p-2.5 rounded-xl border transition ${
        state === FILE_STATES.ERROR 
          ? 'border-red-200 bg-red-50' 
          : state === FILE_STATES.COMPLETE
          ? 'border-emerald-200 bg-emerald-50'
          : 'border-slate-200 bg-white'
      }`}
      data-testid={`upload-item-${file.name}`}
    >
      {/* Thumbnail/Icon */}
      <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 flex items-center justify-center">
        {thumbnail ? (
          <img src={thumbnail} alt="" className="w-full h-full object-cover" />
        ) : (
          <FileText className="h-6 w-6 text-slate-400" />
        )}
      </div>
      
      {/* File Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-slate-700 truncate max-w-[120px]">
            {file.name}
          </span>
          <Badge variant="secondary" className={`text-[9px] px-1.5 py-0 ${categoryConfig.color} text-white`}>
            {categoryConfig.label}
          </Badge>
        </div>
        
        <div className="text-[10px] text-slate-500 mb-1.5">
          {(file.size / 1024).toFixed(0)} KB
        </div>
        
        {/* Progress Bar */}
        {state === FILE_STATES.UPLOADING && (
          <Progress value={progress} className="h-1.5" />
        )}
        
        {/* State Indicator */}
        {state === FILE_STATES.COMPLETE && (
          <div className="flex items-center gap-1 text-emerald-600 text-[10px]">
            <Check className="h-3 w-3" />
            Uploaded
          </div>
        )}
        {state === FILE_STATES.ERROR && (
          <div className="flex items-center gap-1 text-red-600 text-[10px]">
            <AlertCircle className="h-3 w-3" />
            Failed
          </div>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1">
        {state === FILE_STATES.ERROR && (
          <button
            onClick={onRetry}
            className="p-2 rounded-lg bg-amber-100 text-amber-600 active:bg-amber-200 touch-manipulation"
            title="Retry"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        )}
        <button
          onClick={onRemove}
          className="p-2 rounded-lg bg-slate-100 text-slate-500 active:bg-slate-200 touch-manipulation"
          title="Remove"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
});

/**
 * Evidence Completeness Tracker
 */
const EvidenceCompleteness = memo(function EvidenceCompleteness({ evidence, requiredCategories }) {
  const categories = requiredCategories || ['before', 'after'];
  
  return (
    <div className="bg-slate-50 rounded-xl p-3 space-y-2" data-testid="evidence-completeness">
      <h4 className="text-xs font-semibold text-slate-700">Evidence Completeness</h4>
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(EVIDENCE_CATEGORIES).map(([key, config]) => {
          const hasEvidence = evidence.some(e => e.category === key);
          const isRequired = categories.includes(key);
          
          if (!isRequired && !hasEvidence) return null;
          
          return (
            <div 
              key={key}
              className={`flex items-center gap-2 p-2 rounded-lg text-xs ${
                hasEvidence 
                  ? 'bg-emerald-100 text-emerald-700' 
                  : isRequired 
                  ? 'bg-red-100 text-red-700'
                  : 'bg-slate-100 text-slate-500'
              }`}
            >
              {hasEvidence ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <AlertCircle className="h-3.5 w-3.5" />
              )}
              <span>{config.label}</span>
              {isRequired && !hasEvidence && (
                <span className="text-[9px] ml-auto">Missing</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

/**
 * Main Evidence Upload Panel
 */
export default function EvidenceUploadPanel({
  jobId,
  existingEvidence = [],
  requiredCategories = ['before', 'after'],
  onUploadComplete,
  onEvidenceChange,
  allowSubmit = true,
  className = ""
}) {
  const [files, setFiles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('after');
  const [uploading, setUploading] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [expanded, setExpanded] = useState(true);
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  // Check if all required evidence is present
  const allEvidence = [...existingEvidence, ...files.filter(f => f.state === FILE_STATES.COMPLETE)];
  const missingRequired = requiredCategories.filter(
    cat => !allEvidence.some(e => e.category === cat)
  );
  const canSubmit = allowSubmit && missingRequired.length === 0;
  
  // Handle file selection
  const handleFileSelect = useCallback(async (e, fromCamera = false) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;
    
    // Process files with thumbnails
    const processedFiles = await Promise.all(
      selectedFiles.map(async (file) => {
        const thumbnail = await generateThumbnail(file);
        return {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          file,
          thumbnail,
          category: selectedCategory,
          progress: 0,
          state: FILE_STATES.PENDING,
          fromCamera
        };
      })
    );
    
    setFiles(prev => [...prev, ...processedFiles]);
    
    // Auto-upload
    processedFiles.forEach(pf => uploadFile(pf));
    
    // Reset input
    e.target.value = '';
  }, [selectedCategory]);
  
  // Upload single file
  const uploadFile = useCallback(async (fileItem) => {
    setFiles(prev => prev.map(f => 
      f.id === fileItem.id 
        ? { ...f, state: FILE_STATES.UPLOADING, progress: 0 }
        : f
    ));
    
    try {
      // Compress image before upload
      const compressedBlob = await compressImage(fileItem.file);
      const compressedFile = new File(
        [compressedBlob], 
        fileItem.file.name, 
        { type: compressedBlob.type || fileItem.file.type }
      );
      
      const formData = new FormData();
      formData.append('file', compressedFile);
      formData.append('category', fileItem.category);
      formData.append('job_id', jobId);
      
      const response = await axios.post(
        `${API}/api/phs/jobs/${jobId}/evidence`,
        formData,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setFiles(prev => prev.map(f => 
              f.id === fileItem.id ? { ...f, progress } : f
            ));
          }
        }
      );
      
      setFiles(prev => prev.map(f => 
        f.id === fileItem.id 
          ? { ...f, state: FILE_STATES.COMPLETE, progress: 100, serverId: response.data.evidence_id }
          : f
      ));
      
      toast.success(`${fileItem.file.name} uploaded`);
      onUploadComplete?.(response.data);
      onEvidenceChange?.([...existingEvidence, { ...response.data, category: fileItem.category }]);
      
    } catch (err) {
      console.error('Upload failed:', err);
      setFiles(prev => prev.map(f => 
        f.id === fileItem.id 
          ? { ...f, state: FILE_STATES.ERROR, error: err.message }
          : f
      ));
      
      // Handle rate limiting
      if (err.response?.status === 429) {
        toast.error('Upload rate limit reached. Please wait.');
      } else {
        toast.error(`Failed to upload ${fileItem.file.name}`);
      }
    }
  }, [jobId, headers, existingEvidence, onUploadComplete, onEvidenceChange]);
  
  // Retry failed upload
  const handleRetry = useCallback((fileId) => {
    const fileItem = files.find(f => f.id === fileId);
    if (fileItem) {
      uploadFile(fileItem);
    }
  }, [files, uploadFile]);
  
  // Remove file
  const handleRemove = useCallback((fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);
  
  // Handle drag and drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length) {
      handleFileSelect({ target: { files: droppedFiles } });
    }
  }, [handleFileSelect]);
  
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  
  // Calculate stats
  const uploadingCount = files.filter(f => f.state === FILE_STATES.UPLOADING).length;
  const errorCount = files.filter(f => f.state === FILE_STATES.ERROR).length;
  const completeCount = files.filter(f => f.state === FILE_STATES.COMPLETE).length;
  
  return (
    <div 
      className={`bg-white rounded-2xl border border-slate-200 overflow-hidden ${className}`}
      data-testid="evidence-upload-panel"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 border-b border-slate-100 active:bg-slate-100 touch-manipulation"
      >
        <div className="flex items-center gap-2">
          <Upload className="h-4 w-4 text-teal-600" />
          <span className="font-semibold text-slate-800 text-sm">Evidence Upload</span>
          {(existingEvidence.length + completeCount) > 0 && (
            <Badge variant="secondary" className="text-[10px]">
              {existingEvidence.length + completeCount} files
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {missingRequired.length > 0 && (
            <Badge variant="destructive" className="text-[9px]">
              {missingRequired.length} missing
            </Badge>
          )}
          {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </div>
      </button>
      
      {expanded && (
        <div className="p-3 sm:p-4 space-y-4">
          {/* Evidence Completeness Tracker */}
          <EvidenceCompleteness 
            evidence={allEvidence} 
            requiredCategories={requiredCategories} 
          />
          
          {/* Category Selector */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-600">Upload Category</label>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(EVIDENCE_CATEGORIES).map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition touch-manipulation ${
                    selectedCategory === key
                      ? `${config.color} text-white`
                      : 'bg-slate-100 text-slate-600 active:bg-slate-200'
                  }`}
                >
                  {config.label}
                  {config.required && <span className="text-[9px] ml-1">*</span>}
                </button>
              ))}
            </div>
          </div>
          
          {/* Upload Actions - Camera First */}
          <div className="grid grid-cols-2 gap-2">
            {/* Take Photo (Camera - Mobile Primary) */}
            <button
              onClick={() => cameraInputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-teal-300 bg-teal-50 text-teal-700 active:bg-teal-100 touch-manipulation transition"
              data-testid="take-photo-btn"
            >
              <Camera className="h-8 w-8" />
              <span className="text-xs font-medium">Take Photo</span>
            </button>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => handleFileSelect(e, true)}
              className="hidden"
            />
            
            {/* Choose Existing */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 text-slate-600 active:bg-slate-100 touch-manipulation transition"
              data-testid="choose-file-btn"
            >
              <FolderOpen className="h-8 w-8" />
              <span className="text-xs font-medium">Choose Files</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf,.doc,.docx"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
          
          {/* Drag & Drop Zone (Desktop) */}
          <div className="hidden sm:block p-4 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 text-center">
            <p className="text-xs text-slate-500">
              Drag and drop files here, or use the buttons above
            </p>
          </div>
          
          {/* Upload Queue */}
          {files.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-600">Upload Queue</span>
                <div className="flex items-center gap-2 text-[10px]">
                  {uploadingCount > 0 && (
                    <span className="text-blue-600">
                      <RefreshCw className="h-3 w-3 inline animate-spin mr-1" />
                      {uploadingCount} uploading
                    </span>
                  )}
                  {errorCount > 0 && (
                    <span className="text-red-600">{errorCount} failed</span>
                  )}
                  {completeCount > 0 && (
                    <span className="text-emerald-600">{completeCount} complete</span>
                  )}
                </div>
              </div>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {files.map((fileItem) => (
                  <UploadItem
                    key={fileItem.id}
                    file={fileItem.file}
                    thumbnail={fileItem.thumbnail}
                    progress={fileItem.progress}
                    state={fileItem.state}
                    category={fileItem.category}
                    onRemove={() => handleRemove(fileItem.id)}
                    onRetry={() => handleRetry(fileItem.id)}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Existing Evidence Gallery */}
          {existingEvidence.length > 0 && (
            <div className="space-y-2">
              <span className="text-xs font-medium text-slate-600">Existing Evidence</span>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {existingEvidence.map((evidence, idx) => (
                  <div 
                    key={evidence.file_id || idx}
                    className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 group"
                  >
                    {evidence.type === 'photo' || evidence.filename?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                      <LazyEvidenceImage 
                        src={`${API}/api/uploads/job_evidence/${evidence.file_id}`}
                        alt={evidence.filename || 'Evidence'}
                        className="w-full h-full"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileText className="h-6 w-6 text-slate-400" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                      <Eye className="h-5 w-5 text-white" />
                    </div>
                    <Badge 
                      className={`absolute bottom-1 left-1 text-[8px] px-1 py-0 ${
                        EVIDENCE_CATEGORIES[evidence.category]?.color || 'bg-slate-500'
                      } text-white`}
                    >
                      {EVIDENCE_CATEGORIES[evidence.category]?.label || evidence.type || 'File'}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Submit Warning */}
          {!canSubmit && missingRequired.length > 0 && (
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-xs">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Missing required evidence:</p>
                  <ul className="mt-1 list-disc list-inside">
                    {missingRequired.map(cat => (
                      <li key={cat}>{EVIDENCE_CATEGORIES[cat]?.label || cat}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { EVIDENCE_CATEGORIES, FILE_STATES, EvidenceCompleteness };
